import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, fileUrl, formatApiError } from "@/lib/api";
import Header from "@/components/Header";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Heart, MessageCircle, ArrowLeft, Trash2, Reply, Pin, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ReportButton from "@/components/ReportButton";
import TesterBadge from "@/components/TesterBadge";
import { formatDistanceToNow } from "date-fns";

const CAT_LABELS = {
  recipes: "Recipes",
  decorating: "Decorating",
  "business-tips": "Business",
  "show-and-tell": "Show & Tell",
  troubleshooting: "Troubleshoot",
  "testers-chat": "Tester Chat",
};

export default function PostDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [posting, setPosting] = useState(false);
  const [replyTo, setReplyTo] = useState(null); // { id, username }
  const [replyBody, setReplyBody] = useState("");
  const [activeImage, setActiveImage] = useState(0);

  const load = async () => {
    try {
      const [p, c] = await Promise.all([api.get(`/posts/${id}`), api.get(`/posts/${id}/comments`)]);
      setPost(p.data);
      setComments(c.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Post not found");
      nav("/");
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const like = async () => {
    if (!user || user === false) {
      nav("/login");
      return;
    }
    try {
      const { data } = await api.post(`/posts/${id}/like`);
      setPost((p) => ({ ...p, ...data }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    if (!user || user === false) {
      nav("/login");
      return;
    }
    setPosting(true);
    try {
      const { data } = await api.post(`/posts/${id}/comments`, { body: newComment });
      setComments((cs) => [...cs, data]);
      setNewComment("");
      setPost((p) => ({ ...p, comment_count: p.comment_count + 1 }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setPosting(false);
    }
  };

  const submitReply = async (parentId) => {
    if (!replyBody.trim()) return;
    if (!user || user === false) {
      nav("/login");
      return;
    }
    try {
      const { data } = await api.post(`/posts/${id}/comments`, { body: replyBody, parent_id: parentId });
      setComments((cs) => [...cs, data]);
      setReplyBody("");
      setReplyTo(null);
      setPost((p) => ({ ...p, comment_count: p.comment_count + 1 }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deletePost = async () => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/posts/${id}`);
      toast.success("Post deleted");
      nav("/");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/comments/${commentId}`);
      setComments((cs) => cs.map((c) => c.id === commentId ? { ...c, is_deleted: true, body: "[deleted]" } : c));
      toast.success("Comment deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!post) {
    return (
      <>
        <Header />
        <div className="max-w-3xl mx-auto p-8 flex items-center gap-3 text-[#857163]">
          <div className="cp-cakepop-loader" /> Loading…
        </div>
      </>
    );
  }

  const canDelete = user && user !== false && (user.id === post.author.id || user.role === "admin");
  const timeAgo = (() => {
    try {
      return formatDistanceToNow(new Date(post.created_at), { addSuffix: true });
    } catch {
      return "";
    }
  })();

  return (
    <>
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <button onClick={() => nav(-1)} className="cp-btn-ghost inline-flex items-center gap-1.5 mb-4" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <article className="cp-card p-6 sm:p-8" data-testid={`post-detail-${post.id}`}>
          <div className="flex items-center gap-3 mb-4">
            <Avatar className="w-11 h-11 border border-[#EBDCD1]">
              <AvatarImage src={fileUrl(post.author.avatar_url)} />
              <AvatarFallback className="bg-[#FFD07F] text-[#3B2C24] font-semibold text-xs">
                {post.author.username?.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <Link to={`/u/${post.author.username}`} className="font-semibold text-[#3B2C24] hover:underline">
                @{post.author.username}
              </Link>
              {post.author?.is_tester && <TesterBadge size="xs" />}
              <div className="text-xs text-[#857163] flex items-center gap-1.5 flex-wrap">
                <span className="cp-tag">{CAT_LABELS[post.category] || post.category}</span>
                {post.is_pinned && (
                  <span className="cp-tag" style={{ background: "rgba(255, 126, 103, 0.18)" }}>
                    <Pin className="w-3 h-3" /> Pinned
                  </span>
                )}
                {post.is_locked && (
                  <span className="cp-tag" style={{ background: "rgba(59, 44, 36, 0.10)" }}>
                    <Lock className="w-3 h-3" /> Locked
                  </span>
                )}
                <span>{timeAgo}</span>
              </div>
            </div>
            {user && user !== false && user.id !== post.author.id && (
              <ReportButton postId={post.id} />
            )}
            {canDelete && (
              <button onClick={deletePost} className="cp-btn-ghost text-red-500" data-testid="delete-post-btn">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-[#3B2C24] leading-tight">{post.title}</h1>

          {(() => {
            const gallery = post.images?.length ? post.images : (post.image_url ? [post.image_url] : []);
            if (!gallery.length) return null;
            if (gallery.length === 1) {
              return (
                <img
                  src={fileUrl(gallery[0])}
                  alt=""
                  className="w-full max-h-[500px] object-cover rounded-2xl border border-[#EBDCD1] mt-6"
                  data-testid="post-image-0"
                />
              );
            }
            // 2+ images: cover + thumbnail strip
            return (
              <div className="mt-6" data-testid="post-gallery">
                <img
                  src={fileUrl(gallery[activeImage] || gallery[0])}
                  alt=""
                  className="w-full max-h-[500px] object-cover rounded-2xl border border-[#EBDCD1]"
                  data-testid="post-image-cover"
                />
                <div className="mt-2 grid grid-cols-6 gap-2">
                  {gallery.map((path, i) => (
                    <button
                      key={path}
                      onClick={() => setActiveImage(i)}
                      className={`aspect-square rounded-xl overflow-hidden border-2 transition ${
                        activeImage === i ? "border-[#FF7E67]" : "border-transparent opacity-80 hover:opacity-100"
                      }`}
                      data-testid={`post-image-thumb-${i}`}
                    >
                      <img src={fileUrl(path)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

          <div className="mt-6 text-[#3B2C24] leading-relaxed whitespace-pre-wrap">{post.body}</div>

          {post.tags?.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-1.5">
              {post.tags.map((t) => (
                <span key={t} className="cp-tag">#{t}</span>
              ))}
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-[#EBDCD1] flex items-center gap-4">
            <button
              onClick={like}
              className={`flex items-center gap-1.5 text-sm font-medium ${
                post.liked_by_me ? "text-[#FF7E67]" : "text-[#857163] hover:text-[#FF7E67]"
              }`}
              data-testid="detail-like-btn"
            >
              <Heart className={`w-5 h-5 ${post.liked_by_me ? "fill-[#FF7E67]" : ""}`} />
              <span>{post.like_count}</span>
            </button>
            <div className="flex items-center gap-1.5 text-sm text-[#857163]">
              <MessageCircle className="w-5 h-5" /> {post.comment_count}
            </div>
          </div>
        </article>

        <section className="mt-8" data-testid="comments-section">
          <h2 className="font-heading text-2xl font-semibold text-[#3B2C24] mb-4">
            {comments.length} Comment{comments.length === 1 ? "" : "s"}
          </h2>

          {post.is_locked ? (
            <div className="cp-card p-4 mb-6 text-center text-[#857163] bg-[#F9EFE6]/50" data-testid="locked-notice">
              <Lock className="w-5 h-5 inline mr-1.5 -mt-1" />
              This post is locked. New comments are disabled.
            </div>
          ) : user && user !== false ? (
            <form onSubmit={submitComment} className="cp-card p-4 mb-6" data-testid="comment-form">
              <textarea
                className="cp-input min-h-[90px] resize-none"
                placeholder="Share your thoughts…"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                data-testid="comment-input"
              />
              <div className="mt-3 flex justify-end">
                <button
                  type="submit"
                  disabled={posting || !newComment.trim()}
                  className="cp-btn-primary disabled:opacity-50"
                  data-testid="comment-submit"
                >
                  {posting ? "Posting…" : "Post comment"}
                </button>
              </div>
            </form>
          ) : (
            <div className="cp-card p-4 mb-6 text-center text-[#857163]">
              <Link to="/login" className="text-[#FF7E67] font-semibold hover:underline">Log in</Link> to join the conversation.
            </div>
          )}

          <div className="space-y-3">
            {(() => {
              const byParent = {};
              const roots = [];
              comments.forEach((c) => {
                if (c.parent_id) {
                  (byParent[c.parent_id] = byParent[c.parent_id] || []).push(c);
                } else {
                  roots.push(c);
                }
              });
              const renderComment = (c, depth = 0) => (
                <div key={c.id} className={`cp-card p-4 ${depth > 0 ? "ml-6 sm:ml-10" : ""} ${c.is_deleted ? "opacity-60" : ""}`} data-testid={`comment-${c.id}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <Avatar className="w-7 h-7 border border-[#EBDCD1]">
                      <AvatarImage src={fileUrl(c.author.avatar_url)} />
                      <AvatarFallback className="bg-[#FFD07F] text-[#3B2C24] font-semibold text-[10px]">
                        {c.author.username?.slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <Link to={`/u/${c.author.username}`} className="font-semibold text-sm text-[#3B2C24] hover:underline">
                      @{c.author.username}
                    </Link>
                    {c.author?.is_tester && <TesterBadge size="xs" />}
                    <span className="text-xs text-[#857163]">
                      {(() => {
                        try { return formatDistanceToNow(new Date(c.created_at), { addSuffix: true }); }
                        catch { return ""; }
                      })()}
                    </span>
                    {user && user !== false && !c.is_deleted && (user.id === c.author.id || user.role === "admin") && (
                      <button
                        onClick={() => deleteComment(c.id)}
                        className="ml-auto text-[#857163] hover:text-red-500 transition"
                        title="Delete comment"
                        data-testid={`delete-comment-${c.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <div className={`text-sm whitespace-pre-wrap pl-9 ${c.is_deleted ? "italic text-[#857163]" : "text-[#3B2C24]"}`}>{c.body}</div>
                  {user && user !== false && !c.is_deleted && (
                    <div className="pl-9 mt-2">
                      <button
                        onClick={() => { setReplyTo({ id: c.id, username: c.author.username }); setReplyBody(""); }}
                        className="text-xs text-[#857163] hover:text-[#FF7E67] inline-flex items-center gap-1"
                        data-testid={`reply-btn-${c.id}`}
                      >
                        <Reply className="w-3 h-3" /> Reply
                      </button>
                    </div>
                  )}
                  {replyTo?.id === c.id && (
                    <div className="pl-9 mt-3" data-testid={`reply-form-${c.id}`}>
                      <textarea
                        className="cp-input min-h-[70px] resize-none text-sm"
                        placeholder={`Reply to @${c.author.username}…`}
                        value={replyBody}
                        onChange={(e) => setReplyBody(e.target.value)}
                        data-testid={`reply-input-${c.id}`}
                      />
                      <div className="mt-2 flex gap-2 justify-end">
                        <button onClick={() => setReplyTo(null)} className="cp-btn-ghost text-sm">Cancel</button>
                        <button
                          onClick={() => submitReply(c.id)}
                          className="cp-btn-primary text-sm"
                          data-testid={`reply-submit-${c.id}`}
                        >
                          Reply
                        </button>
                      </div>
                    </div>
                  )}
                  {(byParent[c.id] || []).map((child) => renderComment(child, depth + 1))}
                </div>
              );
              return roots.map((c) => renderComment(c));
            })()}
          </div>
        </section>
      </main>
    </>
  );
}
